import { useEffect, useState } from 'react';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HubIcon from '@mui/icons-material/Hub';
import DevicesIcon from '@mui/icons-material/Devices';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import ErrorMessage from '@/components/errorMessage';

const StatCard = ({ title, value, icon, subtitle }: any) => (
  <Card
    className="glass-card-light"
    sx={{
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
      },
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          bgcolor: 'primary.light',
          p: 0.75,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.8,
          '& .MuiSvgIcon-root': { fontSize: 18, color: 'primary.main' },
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          letterSpacing: 1,
        }}
      >
        {title}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', mb: 0.5, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.7rem', mt: 0.5, opacity: 0.8 }}
      >
        {subtitle}
      </Typography>
    )}
  </Card>
);

type Props = { entity: any; maxHeight: number };

const usageCache: Record<string, any> = {};
const fetchGuard: Record<string, boolean> = {};

const EntityUsageStats = ({ entity, maxHeight }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const [dataSourceStats, setDataSourceStats] = useState<any>(usageCache[entity?.id]);
  const [loading, setLoading] = useState<boolean>(!usageCache[entity?.id]);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getDataSourceInstanceUsageStats = async () => {
      if (!entity?.id || fetchGuard[entity.id]) return;
      fetchGuard[entity.id] = true;

      setError(null);
      if (!usageCache[entity.id]) {
        setLoading(true);
      }

      try {
        const stats = await apiContext.getDataSourceInstanceUsageStats(entity.dataSourceDefId, entity.id);
        usageCache[entity.id] = stats;
        setDataSourceStats(stats);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
        fetchGuard[entity.id] = false;
      }
    };

    if (entity?.id) {
      getDataSourceInstanceUsageStats();
    }
  }, [entity?.id, entity?.dataSourceDefId]);

  return (
    <Stack
      spacing={2}
      sx={{ width: '100%', height: maxHeight || '100%', overflowY: 'auto', overflowX: 'hidden', p: 0.5 }}
    >
      {error && <ErrorMessage error={error} />}

      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Total Users"
            value={dataSourceStats?.users || 0}
            icon={<PeopleOutlineIcon color="primary" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title="Subscribers"
            value={dataSourceStats?.subscribers || 0}
            icon={<DynamicFeedIcon color="primary" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard title="User Apps" value={dataSourceStats?.userApps || 0} icon={<DevicesIcon color="primary" />} />
        </Grid>

        {dataSourceStats?.dataSourceItemStats && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Items"
                value={dataSourceStats.dataSourceItemStats.total || 0}
                icon={<StorageIcon color="secondary" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Processed"
                value={dataSourceStats.dataSourceItemStats.processed || 0}
                icon={<CheckCircleOutlineIcon color="primary" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Unprocessed"
                value={dataSourceStats.dataSourceItemStats.unprocessed || 0}
                icon={<TimelineIcon color="info" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Failed"
                value={dataSourceStats.dataSourceItemStats.number || 0}
                icon={<ErrorOutlineIcon color="error" />}
                subtitle={
                  dataSourceStats.dataSourceItemStats.lastProcessed
                    ? `Last: ${dataSourceStats.dataSourceItemStats.lastProcessed}`
                    : undefined
                }
              />
            </Grid>
          </>
        )}
      </Grid>
    </Stack>
  );
};

export default EntityUsageStats;
